import { atom, useAtomValue, useSetAtom } from "jotai";
import {
  isDraftModeIncludingTemplatesAtom,
  nodesAtom,
  canvasIdAtom,
  edgesAtom,
} from "@zapier/canvas-state";
import {
  CopilotProvider as CopilotProviderAiToolkit,
  useConversationId,
} from "@zapier/ai-toolkit/client";
import {
  onToolCallAtom,
  toolsAtom,
  getBodyCommand,
  getRegistryAtom,
  profileAtom,
} from "@zapier/copilot-tools";
import { useReactFlow } from "@xyflow/react";
import { aiPromptAtomResettable } from "@zapier/streaming";

import { useSubmitAndWait } from "../CanvasCopilotHydrator/useSubmitAndWait";
import { initialMessagesAtom } from "../CanvasCopilotHydrator/initialMessagesAtom";

export function CopilotProvider({ children }: { children: React.ReactNode }) {
  // Please please please do not use useAtomValue or useAtom in this component
  // for state that changes very frequently. If you need to pass something to the copilot,
  // use the getBodyCommand.
  const initialMessages = useAtomValue(initialMessagesAtom);
  const onToolCall = useSetAtom(onToolCallAtom);
  const tools = useAtomValue(toolsAtom);
  const getBody = useSetAtom(getBodyCommand);
  const registry = useAtomValue(getRegistryAtom);
  const getHandoffData = useSetAtom(getHandoffDataAtomCommand);
  const reactFlowInstance = useReactFlow();
  const conversationId = useConversationId();
  const profile = useAtomValue(profileAtom);
  const canvasId = useAtomValue(canvasIdAtom);
  const aiPrompt = useAtomValue(aiPromptAtomResettable);

  const submitAndWait = useSubmitAndWait({
    api: "/api/zsl/v2/combined_stream",
    onError: (error: string) => console.error("Error:", error),
  });

  const isDraftModeIncludingTemplates = useAtomValue(
    isDraftModeIncludingTemplatesAtom,
  );

  const api = getApi({
    aiPrompt,
    isDraftModeIncludingTemplates,
  });

  return (
    <CopilotProviderAiToolkit
      profile={profile}
      api={api}
      getHandoffData={getHandoffData}
      loadMessagesEndpoint="/api/canvasui/chat"
      clientTools={tools}
      onToolCall={({ toolCall }) =>
        onToolCall({
          toolCall,
          conversationId,
          reactFlowInstance,
          submitAndWait,
        })
      }
      initialMessages={initialMessages}
      registry={registry}
      copilotId="canvas"
      initialConversationId={conversationId}
      maxSteps={100}
      isExperimentalTodoListEnabled={true}
      feedbackConfig={{
        url: "/api/canvasui/chat/feedback",
      }}
      getBody={getBody}
      onError={(error) => {
        console.error("Canvas copilot error:", error);
      }}
      assetId={canvasId}
    >
      {children}
    </CopilotProviderAiToolkit>
  );
}

const getHandoffDataAtomCommand = atom(null, (get) => {
  const nodes = get(nodesAtom);
  const canvasId = get(canvasIdAtom);
  const edges = get(edgesAtom);
  return {
    nodes,
    edges,
    canvasId,
  };
});
getHandoffDataAtomCommand.debugLabel = "getHandoffDataAtomCommand";

function getApi({
  aiPrompt,
  isDraftModeIncludingTemplates,
}: {
  aiPrompt: string;
  isDraftModeIncludingTemplates: boolean;
}) {
  /** We need the aiPrompt check or else the plan from initialMessages will be gone due to the API changing
   * https://cdn.zappy.app/f0e94a294b8c91c863e88fb39748a832.png
   */
  return isDraftModeIncludingTemplates || aiPrompt
    ? "/api/canvasui/chat/completionsv2"
    : "/api/canvasui/chat/buildcompletions";
}
